import React from 'react';
import {
  LayoutGrid,
  DollarSign,
  Building2,
  BarChart3,
  Cloud,
  FileSpreadsheet,
  ClipboardList,
  Layers,
  Target,
  Zap,
  FileCheck,
  TrendingUp,
  AlertCircle,
} from 'lucide-react';
import { deckImage } from '../components/deck/deckAssets';

/** Norm King Construction stack — tools they use */
const STACK_ITEMS = [
  { icon: ClipboardList, label: 'PM / Field', value: 'Fieldwire', color: 'bg-blue-500' },
  { icon: DollarSign, label: 'Accounting', value: 'QuickBooks QBO', color: 'bg-green-500' },
  { icon: Building2, label: 'Build', value: 'ACC Build', color: 'bg-amber-500' },
  { icon: BarChart3, label: 'ERP / Operations', value: 'Viewpoint', color: 'bg-indigo-500' },
  { icon: Cloud, label: 'File storage', value: 'Dropbox', color: 'bg-sky-500' },
  { icon: FileSpreadsheet, label: 'Heavy use', value: 'Spreadsheets & more', color: 'bg-emerald-600', highlight: true },
];

/**
 * Client Discovery Session page tailored for Norm King Construction.
 * Visual layout with stack overview and masonry/small-contractor use cases.
 * Access at /discover-normking (e.g. https://demo.jobotics.ai/discover-normking).
 */
export default function DiscoverNormKing() {
  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      {/* Hero — light background so logo is visible */}
      <header className="relative bg-gradient-to-br from-white via-slate-50 to-primary-50 text-gray-900 overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%231e293b\' fill-opacity=\'0.04\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-80" aria-hidden="true" />
        <div className="relative mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-16">
          <img src={deckImage('/jobotics-logo.png')} alt="Jobotics" className="h-10 w-auto mb-8" />
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl lg:text-5xl">
            Jobotics AI — Client Discovery Session
          </h1>
          <p className="mt-4 text-xl text-gray-700 max-w-2xl">
            Tailored for{' '}
            <a
              href="https://normkingconstruction.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 font-semibold underline underline-offset-4 hover:text-primary-700"
            >
              Norm King Construction
            </a>
            — quality masonry for commercial, residential, and business builds. Family-owned since 1981.
          </p>
          <p className="mt-3 text-gray-600 max-w-2xl">
            Today we explore how Jobotics can connect Fieldwire, QuickBooks, ACC Build, Viewpoint, Dropbox, and your spreadsheets into one intelligence layer — so you spend less time reconciling data and more time delivering projects.
          </p>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-16 space-y-20">
        {/* Your technology stack — visual grid */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
            <LayoutGrid className="h-7 w-7 text-primary-600" />
            Your technology stack
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl">
            A snapshot of the tools that power Norm King today. Jobotics connects across these systems so you get one place to ask questions and automate reporting.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {STACK_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.label}
                  className={`rounded-xl border-2 bg-white p-4 shadow-sm transition hover:shadow-md ${
                    item.highlight ? 'border-primary-500 ring-2 ring-primary-200' : 'border-gray-200'
                  }`}
                >
                  <div className={`inline-flex rounded-lg p-2 ${item.color} text-white`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="mt-3">
                    <p className="text-xs font-medium uppercase tracking-wider text-gray-500">{item.label}</p>
                    <p className="text-sm font-semibold text-gray-900 mt-0.5">{item.value}</p>
                    {item.highlight && (
                      <p className="text-xs text-primary-600 font-medium mt-1">Central to daily work</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Where Jobotics fits */}
        <section className="rounded-2xl border-2 border-primary-200 bg-white p-6 sm:p-8 shadow-lg">
          <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
            <Layers className="h-7 w-7 text-primary-600" />
            Where Jobotics fits
          </h2>
          <p className="text-gray-700 mb-8 max-w-3xl">
            Fieldwire, QuickBooks QBO, ACC Build, Viewpoint, Dropbox, and your spreadsheets each hold part of the picture. Jobotics sits on top: connect once to the systems you use, and get natural-language answers, automated reporting, and AI-driven insights without replacing what already works. We can also connect to other data sources you add over time.
          </p>
          <div className="flex flex-col items-center gap-4 py-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Your sources</p>
            <div className="flex flex-wrap justify-center gap-3">
              {['Fieldwire', 'QuickBooks QBO', 'ACC Build', 'Viewpoint', 'Dropbox', 'Spreadsheets'].map((name) => (
                <div key={name} className="rounded-xl bg-slate-100 px-4 py-3 text-center">
                  <p className="text-sm font-semibold text-slate-800">{name}</p>
                </div>
              ))}
            </div>
            <p className="text-slate-400 text-sm">Jobotics connects here</p>
            <div className="rounded-xl bg-primary-500 text-white px-8 py-5 text-center shadow-md w-full max-w-md">
              <p className="text-xs font-medium text-primary-100">AI layer</p>
              <p className="text-lg font-bold mt-1">Jobotics</p>
              <p className="text-xs text-primary-200 mt-1">Answers · automation · reporting</p>
            </div>
          </div>
        </section>

        {/* Welcome */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Welcome to Jobotics</h2>
          <p className="text-lg leading-relaxed text-gray-700 mb-4">
            This session is the starting point for a smarter way to run your masonry business. Jobotics AI is purpose-built for contractors like Norm King — connecting Fieldwire, QuickBooks, Viewpoint, Dropbox, and your spreadsheets so office and field see the same picture and you spend less time on manual reporting and data hunting.
          </p>
          <p className="text-lg leading-relaxed text-gray-700">
            Today&apos;s conversation is about understanding your world before prescribing a solution.
          </p>
        </section>

        {/* Session objective — visual cards */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
            <Target className="h-7 w-7 text-primary-600" />
            Session objective
          </h2>
          <p className="text-gray-600 mb-8">
            Our goal is to leave this session with a shared picture of your systems, pain points, and a concrete path forward.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { n: '1', title: 'Systems & workflows', desc: 'Map Fieldwire, QBO, ACC, Viewpoint, Dropbox, and spreadsheets to find integration points.' },
              { n: '2', title: 'Fragmentation & bottlenecks', desc: 'Surface where data is siloed and where reporting or spreadsheet work consumes time.' },
              { n: '3', title: 'High-ROI AI opportunities', desc: 'Pinpoint workflows where AI automation delivers the fastest return for a masonry contractor.' },
              { n: '4', title: 'Pilot scope', desc: 'Define a focused, low-risk starting point that builds confidence for broader rollout.' },
            ].map((item) => (
              <div key={item.n} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-primary-700 font-bold text-sm">
                  {item.n}
                </span>
                <h3 className="mt-3 font-semibold text-gray-900">{item.title}</h3>
                <p className="mt-1 text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Industry reality — tailored for masonry */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <AlertCircle className="h-7 w-7 text-primary-600" />
            Industry reality — what we see across contractors
          </h2>
          <p className="text-gray-700 mb-8 max-w-2xl">
            Specialty and masonry contractors often run on Fieldwire, QuickBooks, spreadsheets, and file storage — but the tools don&apos;t talk to each other. Teams pay the price in lost time, delayed decisions, and preventable errors.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: 'Fragmented data', body: 'Project data lives in Fieldwire, QBO, Viewpoint, Dropbox, and spreadsheets. No single source of truth — reconciling it manually is a full-time job.' },
              { title: 'Reporting eats PM time', body: '5–10 hours per week pulling together reports — time that could go to leading projects and quality masonry delivery.' },
              { title: 'Spreadsheets dominate', body: 'Excel and manual entry remain the backbone of many workflows — rework risk, version conflicts, and inefficiency at scale.' },
              { title: 'Decisions behind the curve', body: 'Without real-time visibility into cost, schedule, and risk, leadership decides on stale data — often days or weeks late.' },
            ].map((item) => (
              <div key={item.title} className="rounded-xl border-l-4 border-primary-400 bg-white p-5 shadow-sm">
                <h3 className="font-semibold text-gray-900">{item.title}</h3>
                <p className="text-sm text-gray-600 mt-2">{item.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Core capabilities — visual */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Zap className="h-7 w-7 text-primary-600" />
            Core capabilities
          </h2>
          <p className="text-gray-700 mb-8 max-w-2xl">
            Jobotics delivers four integrated capability areas that sit on top of your existing stack.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: 'AI Answer Engine', desc: 'Natural language queries across Fieldwire, QBO, Viewpoint, Dropbox, and spreadsheets — accurate answers in seconds.', icon: '💬' },
              { title: 'AI Analyst', desc: 'Automated financial and operational insights: job cost, budget variance, cash flow — in real time.', icon: '📊' },
              { title: 'Workflow automation', desc: 'Automated reporting pipelines, alerts, and triggered actions that cut repetitive manual and spreadsheet work.', icon: '⚡' },
              { title: 'AI agents', desc: 'Specialized agents for PM, estimating, and contract review that take action and flag risks.', icon: '🤖' },
            ].map((cap) => (
              <div key={cap.title} className="flex gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <span className="text-2xl">{cap.icon}</span>
                <div>
                  <h3 className="font-semibold text-gray-900">{cap.title}</h3>
                  <p className="text-sm text-gray-600 mt-1">{cap.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Example use cases — tailored for Norm King */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <FileCheck className="h-7 w-7 text-primary-600" />
            Example use cases for Norm King
          </h2>
          <div className="space-y-8">
            <div className="rounded-2xl border-2 border-gray-200 bg-white overflow-hidden shadow-sm">
              <div className="bg-primary-50 px-6 py-3 border-b border-primary-100">
                <h3 className="font-bold text-gray-900">Project intelligence & reporting</h3>
                <p className="text-sm text-gray-600 mt-0.5">Fieldwire + QuickBooks + Viewpoint + spreadsheets</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-6 p-6">
                <div>
                  <h4 className="text-sm font-semibold text-red-700 mb-2">Before</h4>
                  <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                    <li>Manual aggregation across Fieldwire, QBO, spreadsheets</li>
                    <li>5–8 hrs/week per PM on reporting</li>
                    <li>Stale data by the time reports reach leadership</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-green-700 mb-2">After Jobotics</h4>
                  <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                    <li>Automated reporting from connected systems</li>
                    <li>Real-time job cost and financial visibility</li>
                    <li>One place to ask questions across Fieldwire, QBO, Dropbox</li>
                  </ul>
                </div>
              </div>
              <div className="px-6 pb-6 flex flex-wrap gap-2">
                <span className="rounded-lg bg-primary-100 px-3 py-1 text-sm font-medium text-primary-800">90% faster reports</span>
                <span className="rounded-lg bg-primary-100 px-3 py-1 text-sm font-medium text-primary-800">6–8 hrs saved/PM/week</span>
              </div>
            </div>

            <div className="rounded-2xl border-2 border-gray-200 bg-white overflow-hidden shadow-sm">
              <div className="bg-primary-50 px-6 py-3 border-b border-primary-100">
                <h3 className="font-bold text-gray-900">Estimation & bid prep</h3>
                <p className="text-sm text-gray-600 mt-0.5">Labor, materials, and takeoffs — less spreadsheet wrestling</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-6 p-6">
                <div>
                  <h4 className="text-sm font-semibold text-red-700 mb-2">Before</h4>
                  <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                    <li>Estimates built manually in spreadsheets and historical data</li>
                    <li>Re-keying between systems; version drift and errors</li>
                    <li>Bid prep and takeoffs consume days</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-green-700 mb-2">After Jobotics</h4>
                  <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                    <li>AI-powered labor and cost estimation from connected data</li>
                    <li>Consistent, auditable estimates; fewer errors</li>
                    <li>Faster bid cycles and reuse of past project patterns</li>
                  </ul>
                </div>
              </div>
              <div className="px-6 pb-6 flex flex-wrap gap-2">
                <span className="rounded-lg bg-emerald-100 px-3 py-1 text-sm font-medium text-emerald-800">Faster bid cycles</span>
                <span className="rounded-lg bg-emerald-100 px-3 py-1 text-sm font-medium text-emerald-800">Less spreadsheet dependency</span>
              </div>
            </div>

            <div className="rounded-2xl border-2 border-gray-200 bg-white overflow-hidden shadow-sm">
              <div className="bg-primary-50 px-6 py-3 border-b border-primary-100">
                <h3 className="font-bold text-gray-900">Contract review</h3>
              </div>
              <p className="p-6 text-gray-700 text-sm">
                AI automatically reviews incoming contracts, flagging unfavorable payment terms, liability, and insurance gaps against your standards — delivering a prioritized risk summary in minutes instead of days. Especially valuable when you&apos;re balancing multiple commercial and residential projects.
              </p>
            </div>
          </div>
        </section>

        {/* Pilot — visual */}
        <section className="rounded-2xl border-2 border-primary-200 bg-white p-6 sm:p-8 shadow-lg">
          <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
            <TrendingUp className="h-7 w-7 text-primary-600" />
            Pilot approach — value in 2 weeks
          </h2>
          <p className="text-gray-700 mb-8">
            Structured to deliver tangible results in 2 weeks: 3 days consulting and prototype, 1 week production and iteration. We start with the systems that matter most to you — e.g. Fieldwire + QuickBooks + spreadsheets.
          </p>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-6">
              <p className="text-sm font-bold text-amber-800">Days 1–3</p>
              <p className="text-lg font-semibold text-gray-900 mt-1">Consulting & prototype</p>
              <p className="text-gray-700 mt-2 text-sm">
                Discovery alignment, connect priority systems (e.g. Fieldwire, QBO, Dropbox), validate data, and build a working prototype of your first use case.
              </p>
            </div>
            <div className="rounded-xl border-2 border-green-200 bg-green-50 p-6">
              <p className="text-sm font-bold text-green-800">Week 2</p>
              <p className="text-lg font-semibold text-gray-900 mt-1">Production & iteration</p>
              <p className="text-gray-700 mt-2 text-sm">
                Deploy to production, onboard core users, run live against real project data, and iterate daily based on feedback.
              </p>
            </div>
          </div>
          <p className="text-gray-600 mt-6 text-sm">
            Success metrics: <strong>Time saved</strong>, <strong>Adoption rate</strong>, <strong>Decision speed</strong>.
          </p>
        </section>

        {/* Discovery questions — in a card */}
        <section className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 shadow-sm">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Discovery — what we&apos;ll explore</h2>
          <div className="grid sm:grid-cols-2 gap-6 text-gray-700 text-sm">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Systems & data</h3>
              <ul className="space-y-1 list-disc list-inside">
                <li>How do Fieldwire, QBO, Viewpoint, Dropbox, and spreadsheets fit together today?</li>
                <li>Where is most project data stored? How long does a typical data request take?</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Workflow & reporting</h3>
              <ul className="space-y-1 list-disc list-inside">
                <li>What reports are required weekly or monthly — and who prepares them?</li>
                <li>What workflows are still manual in spreadsheets or email?</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Next steps */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Next steps</h2>
          <ol className="space-y-3 text-gray-700">
            <li><strong>1.</strong> Identify initial use case — e.g. Fieldwire + QBO reporting, or estimation/spreadsheet automation.</li>
            <li><strong>2.</strong> Confirm integration systems — Fieldwire, QuickBooks QBO, Dropbox, and/or others to connect first.</li>
            <li><strong>3.</strong> Align on pilot scope & timeline — 2 weeks (3 days consulting & prototype, 1 week production & iteration).</li>
            <li><strong>4.</strong> Kick off implementation — Sign pilot agreement, schedule kickoff, introduce your dedicated Jobotics team.</li>
          </ol>
        </section>

        {/* Thank You / CTA */}
        <section className="rounded-2xl bg-primary-600 text-white p-8 sm:p-10 text-center">
          <h2 className="text-2xl font-bold mb-4">Thank you</h2>
          <p className="text-primary-100 text-lg mb-6 max-w-2xl mx-auto">
            We appreciate the time and candor you brought to today&apos;s session. We look forward to building something exceptional with Norm King Construction — and to connecting your Fieldwire, QuickBooks, Viewpoint, Dropbox, and spreadsheets into one intelligence layer.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://normkingconstruction.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center rounded-lg border-2 border-white px-6 py-3 text-base font-semibold text-white hover:bg-white/10"
            >
              Norm King Construction
            </a>
          </div>
        </section>
      </main>

      <footer className="border-t border-gray-200 bg-white py-6">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 text-center text-sm text-gray-500">
          Jobotics AI — AI-Native Operating System for Contractors. Connecting your data. Automating your work. Accelerating your decisions.
        </div>
      </footer>
    </div>
  );
}
